import React, {useEffect, useRef} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import LocalStorage from '../../../util/localStorage'
import Resources from '../../../data/services/resources'
import {getThirdResource, clearThirdResource} from '../../../data/actions/thirdResource'
import {getProp} from '../../../common/util/util-helpers'
import {getHeatColor} from '../../../common/util/util-formaters'
import ModalDefault from '../../../common/components/modal/modal-default'
import Chart from 'chart.js/auto'

const fmt$ = (v) => `$${Number(v ?? 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`

const BarChart = ({list, miscTotal}) => {
    const canvasRef = useRef(null)
    const chartRef = useRef(null)

    useEffect(() => {
        if (!canvasRef.current) return
        if (chartRef.current) chartRef.current.destroy()

        const items = [...(list ?? [])]
        if (miscTotal > 0) {
            items.push({CustomCode: 'Misc / No Code', TotalAmount: miscTotal, PartsAmount: 0, LaborAmount: 0})
        }

        const labels = items.map(i => i.CustomCode || (i.CodeNumber ? `Code ${i.CodeNumber}` : 'No Code'))
        const partsData = items.map(i => i.PartsAmount ?? 0)
        const laborData = items.map(i => i.LaborAmount ?? 0)
        const otherData = items.map(i => {
            const other = (i.TotalAmount ?? 0) - (i.PartsAmount ?? 0) - (i.LaborAmount ?? 0)
            return other > 0 ? other : 0
        })

        const ctx = canvasRef.current.getContext('2d')
        chartRef.current = new Chart(ctx, {
            type: 'bar',
            data: {
                labels,
                datasets: [
                    {
                        label: 'Parts',
                        data: partsData,
                        backgroundColor: '#3b82f655',
                        borderColor: '#3b82f6',
                        borderWidth: 1,
                        borderRadius: 3,
                    },
                    {
                        label: 'Labor',
                        data: laborData,
                        backgroundColor: '#10b98155',
                        borderColor: '#10b981',
                        borderWidth: 1,
                        borderRadius: 3,
                    },
                    {
                        label: 'Other',
                        data: otherData,
                        backgroundColor: '#f59e0b55',
                        borderColor: '#f59e0b',
                        borderWidth: 1,
                        borderRadius: 3,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: {intersect: false, mode: 'index'},
                plugins: {
                    legend: {
                        display: true,
                        position: 'top',
                        labels: {font: {size: 11}, boxWidth: 12, padding: 14},
                    },
                    tooltip: {
                        backgroundColor: '#fff',
                        titleColor: '#111827',
                        bodyColor: '#4b5563',
                        borderColor: '#d1d5db',
                        borderWidth: 1,
                        padding: 10,
                        cornerRadius: 8,
                        callbacks: {
                            label: (item) => ` ${item.dataset.label}: ${fmt$(item.raw)}`,
                        },
                    },
                },
                scales: {
                    x: {
                        stacked: true,
                        grid: {display: false},
                        ticks: {color: '#6b7280', font: {size: 10}, maxRotation: 45, minRotation: 30},
                        border: {color: '#e5e7eb'},
                    },
                    y: {
                        stacked: true,
                        grid: {color: '#f3f4f6'},
                        ticks: {
                            color: '#6b7280',
                            font: {size: 11},
                            callback: (v) => `$${v.toLocaleString()}`,
                        },
                        border: {display: false},
                    },
                },
            },
        })

        return () => {
            if (chartRef.current) {
                chartRef.current.destroy()
                chartRef.current = null
            }
        }
    }, [list, miscTotal])

    return <canvas ref={canvasRef}/>
}

export default function CostPerCodeDialog({show, onClose, unitId, unitName, isTrailerMode, startDate, endDate, translate}) {
    const dispatch = useDispatch()
    const thirdResource = useSelector((state) => state.thirdResource)
    const isLoading = thirdResource.isLoading
    const list = thirdResource?.data?.list ?? []
    const miscTotal = getProp(thirdResource.data, 'MiscTotal', 0)
    const miscPaidAmount = getProp(thirdResource.data, 'MiscPaidAmount', 0)
    const miscChargedAmount = getProp(thirdResource.data, 'MiscChargedAmount', 0)
    const grandTotalAmount = getProp(thirdResource.data, 'TotalAmount', 0)
    const grandPaidAmount = getProp(thirdResource.data, 'PaidAmount', 0)
    const grandChargedAmount = getProp(thirdResource.data, 'ChargedAmount', 0)

    useEffect(() => {
        if (!show || !unitId) return
        const query = {StartDate: startDate, EndDate: endDate}
        if (isTrailerMode) {
            query.TrailerID = unitId
            query.IsTrailer = 1
        } else {
            query.TruckID = unitId
        }
        dispatch(getThirdResource({
            user: LocalStorage.get('user'),
            resource: Resources.WorkOrdersReportsPerCode,
            query,
        }))
        return () => dispatch(clearThirdResource())
    }, [show, unitId])

    const maxTotal = Math.max(...list.map(r => r.TotalAmount ?? 0), 0)
    const minTotal = Math.min(...list.map(r => r.TotalAmount ?? 0), 0)
    const maxPaid = Math.max(...list.map(r => r.PaidAmount ?? 0), 0)
    const minPaid = Math.min(...list.map(r => r.PaidAmount ?? 0), 0)

    const dateLabel = (() => {
        if (!startDate || !endDate) return ''
        const fmt = (d) => new Date(d).toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})
        return `${fmt(startDate)} – ${fmt(endDate)}`
    })()

    return (
        <ModalDefault
            show={show}
            onClose={onClose}
            title={`Cost per Code — ${unitName ?? ''}`}
            widthClass="max-w-4xl w-full"
            hideDialogFooter
        >
            <div className="p-4 space-y-4">
                {dateLabel && (
                    <p className="text-xs text-tm-gray-400">{dateLabel}</p>
                )}

                {isLoading && (
                    <div className="flex items-center justify-center py-16">
                        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"/>
                    </div>
                )}

                {!isLoading && list.length === 0 && miscTotal === 0 && (
                    <div className="flex items-center justify-center py-16 text-sm text-tm-gray-400">
                        No code data for this {isTrailerMode ? 'trailer' : 'truck'}
                    </div>
                )}

                {!isLoading && (list.length > 0 || miscTotal > 0) && (
                    <>
                        {/* Bar chart */}
                        <div className="md:bg-inverse md:border border-tm-gray-300 md:shadow-card rounded-card">
                            <div className="px-4 pt-3 pb-1">
                                <p className="text-xs font-semibold text-tm-gray-500 uppercase tracking-wide">Cost by Code</p>
                            </div>
                            <div className="p-4" style={{height: 280}}>
                                <BarChart list={list} miscTotal={miscTotal}/>
                            </div>
                        </div>

                        {/* Table */}
                        <div className="md:bg-inverse md:border border-tm-gray-300 md:shadow-card rounded-card overflow-auto">
                            <table className="min-w-full text-sm divide-y divide-tm-gray-300">
                                <thead className="bg-tm-gray-50">
                                <tr>
                                    {['#', 'Code', 'Parts', 'Labor', 'Total', 'Paid', 'Charged'].map((label, i) => (
                                        <th
                                            key={label}
                                            className={`px-4 py-2.5 font-medium text-tm-gray-700 whitespace-nowrap ${i < 2 ? 'text-left' : 'text-right'}`}
                                        >
                                            {label}
                                        </th>
                                    ))}
                                </tr>
                                </thead>
                                <tbody className="divide-y divide-tm-gray-200">
                                {list.map((row, i) => (
                                    <tr key={row.CustomCodeID ?? `no-code-${i}`} className="bg-popup hover:bg-tm-gray-50 transition-colors">
                                        <td className="px-4 py-2.5 text-tm-gray-500 text-xs">{row.CodeNumber || '—'}</td>
                                        <td className="px-4 py-2.5 font-medium text-tm-gray-800">{row.CustomCode || 'No Code'}</td>
                                        <td className="px-4 py-2.5 text-right tabular-nums text-tm-gray-600 text-xs">{fmt$(row.PartsAmount)}</td>
                                        <td className="px-4 py-2.5 text-right tabular-nums text-tm-gray-600 text-xs">{fmt$(row.LaborAmount)}</td>
                                        <td className="px-4 py-2.5 text-right tabular-nums text-xs font-semibold"
                                            style={{color: getHeatColor(row.TotalAmount ?? 0, minTotal, maxTotal)}}>
                                            {fmt$(row.TotalAmount)}
                                        </td>
                                        <td className="px-4 py-2.5 text-right tabular-nums text-xs"
                                            style={{color: getHeatColor(row.PaidAmount ?? 0, minPaid, maxPaid)}}>
                                            {fmt$(row.PaidAmount)}
                                        </td>
                                        <td className="px-4 py-2.5 text-right tabular-nums text-xs text-tm-gray-600">{fmt$(row.ChargedAmount)}</td>
                                    </tr>
                                ))}

                                {miscTotal > 0 && (
                                    <tr className="bg-popup hover:bg-tm-gray-50 transition-colors italic">
                                        <td className="px-4 py-2.5 text-tm-gray-400 text-xs">—</td>
                                        <td className="px-4 py-2.5 text-tm-gray-500">Misc / No Code</td>
                                        <td className="px-4 py-2.5 text-right tabular-nums text-tm-gray-400 text-xs">—</td>
                                        <td className="px-4 py-2.5 text-right tabular-nums text-tm-gray-400 text-xs">—</td>
                                        <td className="px-4 py-2.5 text-right tabular-nums text-xs font-semibold"
                                            style={{color: getHeatColor(miscTotal, minTotal, maxTotal)}}>
                                            {fmt$(miscTotal)}
                                        </td>
                                        <td className="px-4 py-2.5 text-right tabular-nums text-xs"
                                            style={{color: getHeatColor(miscPaidAmount, minPaid, maxPaid)}}>
                                            {fmt$(miscPaidAmount)}
                                        </td>
                                        <td className="px-4 py-2.5 text-right tabular-nums text-xs text-tm-gray-600">{fmt$(miscChargedAmount)}</td>
                                    </tr>
                                )}
                                </tbody>
                                <tfoot>
                                <tr className="bg-tm-gray-50 font-semibold border-t-2 border-tm-gray-300">
                                    <td colSpan={2} className="px-4 py-2.5 text-tm-gray-700">Grand Total</td>
                                    <td className="px-4 py-2.5 text-right tabular-nums text-tm-gray-700 text-xs">
                                        {fmt$(list.reduce((s, r) => s + (r.PartsAmount ?? 0), 0))}
                                    </td>
                                    <td className="px-4 py-2.5 text-right tabular-nums text-tm-gray-700 text-xs">
                                        {fmt$(list.reduce((s, r) => s + (r.LaborAmount ?? 0), 0))}
                                    </td>
                                    <td className="px-4 py-2.5 text-right tabular-nums text-tm-gray-700 text-xs">{fmt$(grandTotalAmount)}</td>
                                    <td className="px-4 py-2.5 text-right tabular-nums text-tm-gray-700 text-xs">{fmt$(grandPaidAmount)}</td>
                                    <td className="px-4 py-2.5 text-right tabular-nums text-tm-gray-700 text-xs">{fmt$(grandChargedAmount)}</td>
                                </tr>
                                </tfoot>
                            </table>
                        </div>
                    </>
                )}
            </div>
        </ModalDefault>
    )
}
